import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { EnvironmentUtil } from './app/core/utils/environment.util';

// ============================================================================
// CONSTANTES DE CONFIGURATION
// ============================================================================

const CONFIG = {
  RELOAD: {
    MAX_ATTEMPTS: 3,
    TIMEOUT_MS: 10000,
    STORAGE_KEYS: {
      COUNT: 'reloadCount',
      LAST_TIME: 'lastReloadTime'
    }
  },
  SERVICE_WORKER: {
    REGISTRATION_DELAY_MS: 3000,
    SCRIPT_PATH: '/ngsw-worker.js',
    SCOPE: '/',
    UPDATE_VIA_CACHE: 'none' as ServiceWorkerUpdateViaCache
  },
  INITIALIZATION: {
    STABILITY_DELAY_MS: 100
  }
} as const;

// ============================================================================
// TYPES ET INTERFACES
// ============================================================================

interface ReloadState {
  count: number;
  lastTime: number;
}

interface ErrorRecoveryOptions {
  shouldReload: boolean;
  shouldClearCache: boolean;
  shouldUnregisterSW: boolean;
}

// ============================================================================
// CLASSE DE GESTION DES ERREURS
// ============================================================================

class ErrorHandler {
  private static readonly MIME_TYPE_ERROR_PATTERNS = [
    'Failed to fetch dynamically imported module',
    'Expected a JavaScript-or-Wasm module script',
    'MIME type of "text/html"'
  ];

  private static readonly CHUNK_ERROR_PATTERNS = [
    'ChunkLoadError',
    'Failed to fetch'
  ];

  /**
   * Vérifie si une erreur est liée au MIME type
   */
  static isMimeTypeError(message: string | null | undefined): boolean {
    if (!message) return false;
    return this.MIME_TYPE_ERROR_PATTERNS.some(pattern => message.includes(pattern));
  }

  /**
   * Vérifie si une erreur est liée au chargement de chunks
   */
  static isChunkLoadError(message: string | null | undefined): boolean {
    if (!message) return false;
    return this.CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
  }

  /**
   * Vérifie si une erreur de réseau peut être ignorée
   */
  static isIgnorableNetworkError(reason: any): boolean {
    return reason?.name === 'TimeoutError' ||
           reason?.statusCode === 503 ||
           reason?.offline === true;
  }

  /**
   * Affiche un message d'erreur à l'utilisateur
   */
  static displayErrorToUser(error: Error, showReloadButton = true): void {
    const loader = document.getElementById('app-loader');
    if (!loader) return;

    const reloadButton = showReloadButton
      ? `<button onclick="location.reload()" 
                 style="background: #6c757d; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin: 10px;">
          Recharger la page
        </button>`
      : '';

    loader.innerHTML = `
      <div style="text-align: center; padding: 20px;">
        <h2 style="color: #dc3545;">❌ Erreur de démarrage</h2>
        <p style="color: #666;">L'application n'a pas pu démarrer correctement.</p>
        <p style="color: #999; font-size: 12px;">${error.message || 'Erreur inconnue'}</p>
        <button onclick="location.href='/clear-cache'" 
                style="background: #007bff; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin: 10px;">
          Vider le cache et réessayer
        </button>
        ${reloadButton}
      </div>
    `;
  }
}

// ============================================================================
// CLASSE DE GESTION DU SERVICE WORKER
// ============================================================================

class ServiceWorkerManager {
  /**
   * Vérifie si le service worker est supporté
   */
  static isSupported(): boolean {
    return 'serviceWorker' in navigator;
  }

  /**
   * Nettoie tous les caches
   */
  static async clearAllCaches(): Promise<void> {
    if (!('caches' in window)) return;

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    } catch (error) {
      console.warn('⚠️ Erreur lors du nettoyage des caches:', error);
    }
  }

  /**
   * Désinscrit tous les service workers
   */
  static async unregisterAll(): Promise<void> {
    if (!this.isSupported()) return;

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    } catch (error) {
      console.warn('⚠️ Erreur lors de la désinscription des service workers:', error);
    }
  }

  /**
   * Nettoie et désinscrit tout (cache + SW)
   */
  static async cleanEverything(): Promise<void> {
    await Promise.all([
      this.clearAllCaches(),
      this.unregisterAll()
    ]);
  }

  /**
   * Vérifie et nettoie le service worker selon l'environnement
   */
  static async checkAndClean(): Promise<void> {
    if (!this.isSupported()) return;

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const shouldEnableSW = EnvironmentUtil.shouldEnableServiceWorker();

      // En développement : désactiver tous les SW
      if (!shouldEnableSW && registrations.length > 0) {
        console.warn('⚠️ Mode développement: Désactivation de tous les service workers...');
        await this.cleanEverything();
        return;
      }

      // En production/staging : nettoyer seulement si plusieurs SW
      if (shouldEnableSW && registrations.length > 1) {
        console.warn('⚠️ Plusieurs service workers détectés, nettoyage...');
        await this.cleanEverything();
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de la vérification du service worker:', error);
    }
  }

  /**
   * Enregistre le service worker Angular
   */
  static async register(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }

    const shouldEnableSW = EnvironmentUtil.shouldEnableServiceWorker();
    if (!shouldEnableSW) {
      EnvironmentUtil.log('ℹ️ Service worker désactivé en mode développement');
      return;
    }

    try {
      // Attendre que l'application soit stable
      await this.waitForStability();

      const registration = await navigator.serviceWorker.register(
        CONFIG.SERVICE_WORKER.SCRIPT_PATH,
        {
          scope: CONFIG.SERVICE_WORKER.SCOPE,
          updateViaCache: CONFIG.SERVICE_WORKER.UPDATE_VIA_CACHE
        }
      );

      EnvironmentUtil.log('✅ Service worker enregistré:', registration.scope);

      // Configurer les gestionnaires d'événements
      this.setupEventHandlers(registration);
    } catch (error) {
      console.error('❌ Erreur lors de l\'enregistrement du service worker:', error);
      // Ne pas bloquer l'application en cas d'erreur
    }
  }

  /**
   * Attend que l'application soit stable avant d'enregistrer le SW
   */
  private static async waitForStability(): Promise<void> {
    return new Promise(resolve => 
      setTimeout(resolve, CONFIG.SERVICE_WORKER.REGISTRATION_DELAY_MS)
    );
  }

  /**
   * Configure les gestionnaires d'événements du service worker
   */
  private static setupEventHandlers(registration: ServiceWorkerRegistration): void {
    // Gérer les mises à jour
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          EnvironmentUtil.log('🔄 Nouvelle version du service worker disponible');
        }
      });
    });

    // Gérer les erreurs de MIME type depuis le SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'MIME_TYPE_ERROR') {
        console.warn('⚠️ Erreur MIME type détectée depuis le service worker');
        this.handleMimeTypeError();
      }
    });
  }

  /**
   * Gère les erreurs de MIME type
   */
  private static async handleMimeTypeError(): Promise<void> {
    await this.cleanEverything();
    window.location.reload();
  }
}

// ============================================================================
// CLASSE DE GESTION DU RECHARGEMENT
// ============================================================================

class ReloadManager {
  /**
   * Récupère l'état actuel du rechargement
   */
  static getState(): ReloadState {
    const count = parseInt(sessionStorage.getItem(CONFIG.RELOAD.STORAGE_KEYS.COUNT) || '0', 10);
    const lastTime = parseInt(sessionStorage.getItem(CONFIG.RELOAD.STORAGE_KEYS.LAST_TIME) || '0', 10);
    return { count, lastTime };
  }

  /**
   * Sauvegarde l'état du rechargement
   */
  static saveState(count: number, lastTime: number): void {
    sessionStorage.setItem(CONFIG.RELOAD.STORAGE_KEYS.COUNT, String(count));
    sessionStorage.setItem(CONFIG.RELOAD.STORAGE_KEYS.LAST_TIME, String(lastTime));
  }

  /**
   * Réinitialise l'état du rechargement
   */
  static resetState(): void {
    sessionStorage.removeItem(CONFIG.RELOAD.STORAGE_KEYS.COUNT);
    sessionStorage.removeItem(CONFIG.RELOAD.STORAGE_KEYS.LAST_TIME);
  }

  /**
   * Vérifie si on peut encore tenter un rechargement
   */
  static canReload(): boolean {
    const state = this.getState();
    const now = Date.now();

    // Reset si le timeout est dépassé
    if (now - state.lastTime > CONFIG.RELOAD.TIMEOUT_MS) {
      this.resetState();
      return true;
    }

    return state.count < CONFIG.RELOAD.MAX_ATTEMPTS;
  }

  /**
   * Incrémente le compteur de rechargement
   */
  static incrementReloadCount(): void {
    const state = this.getState();
    const newCount = state.count + 1;
    this.saveState(newCount, Date.now());
  }

  /**
   * Gère le rechargement avec nettoyage
   */
  static async performReload(options: ErrorRecoveryOptions): Promise<void> {
    if (!this.canReload()) {
      console.error('❌ Nombre maximum de tentatives de rechargement atteint');
      this.resetState();
      window.location.href = '/clear-cache';
      return;
    }

    this.incrementReloadCount();
    const attempt = this.getState().count;

    console.warn(`⚠️ Tentative de récupération (${attempt}/${CONFIG.RELOAD.MAX_ATTEMPTS})...`);

    if (options.shouldClearCache || options.shouldUnregisterSW) {
      await ServiceWorkerManager.cleanEverything();
    }

    window.location.reload();
  }
}

// ============================================================================
// CLASSE DE GESTION DU BOOTSTRAP
// ============================================================================

class BootstrapManager {
  /**
   * Initialise l'application avec gestion d'erreurs robuste
   */
  static async initialize(): Promise<void> {
    try {
      // Afficher les informations d'environnement
      EnvironmentUtil.logEnvironmentInfo();

      // Nettoyer le service worker si nécessaire
      await ServiceWorkerManager.checkAndClean();

      // Attendre la stabilité
      await this.waitForStability();

      // Réinitialiser l'état de rechargement
      ReloadManager.resetState();

      // Bootstraper l'application
      await this.bootstrap();

    } catch (error) {
      console.error('❌ Échec de l\'initialisation:', error);
      this.handleInitializationError(error as Error);
    }
  }

  /**
   * Attend que l'environnement soit stable
   */
  private static async waitForStability(): Promise<void> {
    return new Promise(resolve => 
      setTimeout(resolve, CONFIG.INITIALIZATION.STABILITY_DELAY_MS)
    );
  }

  /**
   * Bootstrape l'application Angular
   */
  private static async bootstrap(): Promise<void> {
    try {
      await bootstrapApplication(AppComponent, appConfig);

      // Enregistrer le service worker après le bootstrap réussi
      // (en arrière-plan, ne pas bloquer)
      ServiceWorkerManager.register().catch(err => {
        console.warn('⚠️ Erreur lors de l\'enregistrement du service worker:', err);
      });

    } catch (error) {
      await this.handleBootstrapError(error as Error);
    }
  }

  /**
   * Gère les erreurs de bootstrap
   */
  private static async handleBootstrapError(error: Error): Promise<void> {
    console.error('❌ Erreur de bootstrap:', error);
    console.error('Stack:', error?.stack);

    if (ErrorHandler.isChunkLoadError(error.message)) {
      await ReloadManager.performReload({
        shouldReload: true,
        shouldClearCache: true,
        shouldUnregisterSW: true
      });
    } else {
      ErrorHandler.displayErrorToUser(error, true);
    }
  }

  /**
   * Gère les erreurs d'initialisation
   */
  private static handleInitializationError(error: Error): void {
    ErrorHandler.displayErrorToUser(error, false);
  }
}

// ============================================================================
// GESTIONNAIRES D'ÉVÉNEMENTS GLOBAUX
// ============================================================================

/**
 * Gestionnaire d'erreurs globales
 */
window.addEventListener('error', (event) => {
  const message = event.error?.message || event.message;
  console.error('❌ Erreur globale capturée:', event.error || message);

  if (ErrorHandler.isMimeTypeError(message)) {
    console.warn('⚠️ Erreur de chargement de module détectée (MIME type)');
    
    ReloadManager.performReload({
      shouldReload: true,
      shouldClearCache: true,
      shouldUnregisterSW: true
    }).catch(err => {
      console.error('❌ Erreur lors de la récupération:', err);
    });
  }
});

/**
 * Gestionnaire de promesses rejetées non gérées
 */
window.addEventListener('unhandledrejection', (event) => {
  console.error('❌ Promesse rejetée non gérée:', event.reason);

  // Empêcher que l'erreur ne casse l'application
  event.preventDefault();

  // Ignorer les erreurs de réseau/timeout non critiques
  if (ErrorHandler.isIgnorableNetworkError(event.reason)) {
    console.warn('⚠️ Erreur réseau/timeout ignorée, continuation de l\'exécution');
    return;
  }
});

// ============================================================================
// POINT D'ENTRÉE PRINCIPAL
// ============================================================================

/**
 * Point d'entrée de l'application
 * Gère l'initialisation complète avec gestion d'erreurs robuste
 */
BootstrapManager.initialize().catch((error) => {
  console.error('❌ Erreur critique lors du démarrage:', error);
  ErrorHandler.displayErrorToUser(
    error instanceof Error ? error : new Error('Erreur inconnue'),
    false
  );
});
